import axios, { AxiosResponse, HttpStatusCode } from 'axios';
import { apiPath } from '@/redux/apiPath';
import { tokenRefreshSchema } from '@/models/tokenRefreshSchema';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import * as authStorage from './authStorage';
import { tokenClient } from './tokenClient';
import { SESSION_EXPIRED_EVENT, TOKEN_REFRESHED_EVENT } from './authEvents';

const REFRESH_AHEAD_OF_EXPIRY_MS = 60_000;

let refreshInFlight: Promise<boolean> | null = null;

export const forceLogout = (): void => {
  authStorage.clearSession();
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

/**
 * Only a 401 means the refresh token itself was rejected (invalid, expired or reused) - the
 * session is over. Anything else (network error, 5xx, 409 for a misconfigured server) is
 * usually temporary and must not log the user out.
 */
const isSessionRejected = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized;

const doRefresh = async (): Promise<boolean> => {
  const refreshToken = authStorage.getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  let response: AxiosResponse;
  try {
    response = await tokenClient.post(apiPath.tokenRefresh(), { refreshToken });
  } catch (error) {
    if (isSessionRejected(error)) {
      forceLogout();
      return false;
    }
    throw error;
  }

  const { data } = response;
  if (!validateDataUsingZodSchema(data, tokenRefreshSchema)) {
    throw new Error('Invalid response from the token refresh endpoint');
  }
  if (authStorage.getRefreshToken() !== refreshToken) {
    // The session was cleared or rotated (e.g. a concurrent logout) while this refresh
    // was in flight — discard the response instead of resurrecting a dead session.
    return false;
  }
  authStorage.setTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    accessTokenExpiresIn: data.accessTokenExpiresIn,
  });
  window.dispatchEvent(new Event(TOKEN_REFRESHED_EVENT));
  return true;
};

/**
 * Resolves to true when the tokens were rotated and to false when there is no session to
 * refresh (anymore) - a rejected refresh token logs the user out first. Rejects when the
 * refresh failed for any other reason; the session is kept, so it can be retried later.
 */
export const refreshTokens = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

/** Refreshes the access token when it is about to expire; rejects like refreshTokens(). */
export const ensureFreshAccessToken = async (): Promise<void> => {
  const deadline = authStorage.getAccessTokenDeadline();
  if (deadline === null) {
    return;
  }
  if (Date.now() >= deadline - REFRESH_AHEAD_OF_EXPIRY_MS) {
    await refreshTokens();
  }
};
